// #region: Class definition *********************************************************************/
/**
 * @class ToolTip
 * @description Custom web component that renders a tooltip with touch handling.
 * 
 * @extends WebComponent
 * Extends the WebComponent base class.
 *
 * @example
 * <tool-tip class="tooltip right">
 *    ...text
 * </tool-tip>
 *
 * @property {string} class - A class to style the tooltip, and set the relative position.
 *
 * @remarks
 *     The component expects proper HTML structure with tooltip content.
*/
class ToolTip extends WebComponent {

    // #region: Private Fields *******************************************************************/
    /**
     * Reference to the currently active tooltip element.
     * @type {HTMLElement|null}
     * 
     * @private
     */
    #activeTooltip;

    /**
     * Flag indicating if the current device supports touch events.
     * @type {boolean}
     * 
     * @private
     */
    #isTouchDevice;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Creates an instance of ToolTip.
     */
    constructor() {
        super();

        /* Constant for the time out, how long should we wait. */
        this.LONG_PRESS_DURATION = 500;

        /* Constant for the time out, how long should we wait. */
        this.HIDE_TIMEOUT = 3000;

        /* No active tooltip to begin with. */
        this.#activeTooltip = null;

        /* Checks if the device supports touch events. */
        this.#isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0);
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * This is used by the browser to identify and register the custom element.
     * @type {string}
     * @static
     * @memberof ToolTip
     */
    static tagName = 'tool-tip';

    /**
     * Identifies class instance.
     * @returns {string} The string representation of this class instance.
     */
    toString() {
        return '[ToolTip]';
    }

    /**
     * Identifies class.
     * @returns {string} The string representation of this class.
     * 
     * @static
     */
    static toString() {
        return '[ToolTip]';
    }

    /**
     * Identifies the version.
     * @returns {string} The version number of this class.
     * 
     * @static
     */
    static version() {
        return '1.0.0';
    }
    // #endregion ********************************************************************************/
 
    // #region: Public methods *******************************************************************/
    /**
     * Handles touch interactions for tooltips.
     * 
     * @memberof ToolTip
     */
    initialize() {
        // Only initialize touch events if we're on a touch device.
        if (!this.#isTouchDevice) {
            return;
        }

        const tooltipElements = document.querySelectorAll('.with-tooltip');
        let touchTimeout;

        tooltipElements.forEach((element) => {
            let isLongPress = false;

            // Start touch timer.
            element.addEventListener('touchstart', () => {
                touchTimeout = setTimeout(() => {
                    isLongPress = true;
                    const tooltip = element.querySelector('tool-tip');

                    if (tooltip) {
                        // Hide previous tooltip if exists.
                        if (this.#activeTooltip && this.#activeTooltip !== tooltip) {
                            this.#activeTooltip.classList.remove('active');
                        }

                        // Set and show new tooltip.
                        this.#activeTooltip = tooltip;

                        tooltip.classList.add('active');

                        // Hide tooltip after this.HIDE_TIMEOUT (3) seconds.
                        setTimeout(() => {
                            tooltip.classList.remove('active');
                            isLongPress = false;

                            if (this.#activeTooltip === tooltip) {
                                this.#activeTooltip = null;
                            }
                        }, this.HIDE_TIMEOUT);
                    }
                }, this.LONG_PRESS_DURATION);
            });

            // Clear touch timer if touch ends early.
            element.addEventListener('touchend', () => {
                clearTimeout(touchTimeout);
                if (!isLongPress) {
                    const tooltip = element.querySelector('tool-tip');

                    if (tooltip) {
                        tooltip.classList.remove('active');
                    }
                }

                isLongPress = false;
            });

            // Cancel tooltip if touch moves.
            element.addEventListener('touchmove', () => {
                clearTimeout(touchTimeout);
                const tooltip = element.querySelector('tool-tip');

                if (tooltip) {
                    tooltip.classList.remove('active');
                }

                isLongPress = false;
            });
        });
    };

    /**
     * Validate the content.
     * @returns {boolean} True if valid, false if not. Defaults to true.
     * 
     * @memberof ToolTip
     */
    validate() {
        return true;
    }
    // #endregion ********************************************************************************/

}
// #endregion ************************************************************************************/

/**
 * Register web component.
 * Defines the custom element 'tool-tip' and maps it to the ToolTip class.
 */
customElements.define(ToolTip.tagName, ToolTip);
